/**
 * Página "Mis inventarios": listas de inventario asignadas al usuario autenticado
 */
import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Card, Typography, Alert, useTheme, useMediaQuery, Divider } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
    DATA_GRID_SX,
    DATA_GRID_SLOT_PROPS,
    DATA_GRID_LOCALE_TEXT,
    DATA_GRID_INITIAL_STATE,
    DATA_GRID_PAGE_SIZE_OPTIONS,
} from "../../../components/ui/dataGridStyles";
import FactCheckIcon from "@mui/icons-material/FactCheck";

import { useMisInventarios, useDrawerInventario } from "../hooks";
import { generateMisInventariosColumns } from "../helpers/columns";
import { MisInventariosMobileList } from "../components/lists";
import { DrawerDetalleInventario } from "../components/drawers";
import { EstadisticasInventarios } from "../components/ui";
import { FiltrosInventario } from "../components/forms";
import { ESTADOS_FINALIZADOS } from "../constants";

// Mismo literal que MisInventariosMobileList.js, para no divergir entre la
// vista de tarjetas (principal) y el fallback de escritorio.
const MENSAJE_SIN_ASIGNADOS = "No tienes inventarios asignados";

const NoRowsOverlay = () => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", p: 3 }}>
        <Typography sx={{ color: "#6c757d", fontSize: 14 }}>{MENSAJE_SIN_ASIGNADOS}</Typography>
    </Box>
);

/**
 * DECISION: a diferencia del resto de pantallas del proyecto (donde el
 * DataGrid es la vista principal y las tarjetas son el fallback móvil), aquí
 * la vista de TARJETAS (MisInventariosMobileList) es la PRINCIPAL y el
 * DataGrid es el fallback de escritorio: el usuario típico de esta pantalla es
 * un preparador de pie en el almacén con el móvil, no un admin sentado frente
 * a un puesto fijo (ver tasks/milestones.md, MI3).
 *
 * Control de acceso: los roles requeridos (ADMIN, REPONEDOR, ALMACEN) se
 * resuelven en src/routes.js, no aquí (ver CLAUDE.md, Estados de Carga, regla 3).
 */
const MisInventarios = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
    const navigate = useNavigate();

    const { inventarios, estadisticas, filtros, aplicarFiltros, loading, error } = useMisInventarios();
    const drawer = useDrawerInventario();

    // Finalizado -> resultado en el drawer; pendiente o en curso -> pantalla de conteo
    const { abrir: abrirDrawer } = drawer;
    const handleVer = useCallback(
        (inventario) => {
            if (ESTADOS_FINALIZADOS.includes(inventario.estado)) abrirDrawer(inventario.id);
            else navigate(`/mis-inventarios/${inventario.id}`);
        },
        [abrirDrawer, navigate]
    );

    const columns = useMemo(() => generateMisInventariosColumns({ onVer: handleVer }), [handleVer]);

    return (
        <Box sx={{ width: "100%" }}>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                {/* Header */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Typography
                        variant={isMobile ? "h6" : "h5"}
                        sx={{ fontWeight: 700, color: "#212529", mb: 0.5, display: "flex", alignItems: "center", gap: 1 }}
                    >
                        <FactCheckIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                        Mis inventarios
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6c757d" }}>
                        Listas de conteo que tienes asignadas
                    </Typography>

                    {estadisticas && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <EstadisticasInventarios
                                estadisticas={estadisticas}
                                estadoActivo={filtros.estado}
                                onSeleccionar={(estado) => aplicarFiltros({ ...filtros, estado })}
                            />
                        </>
                    )}

                    <Divider sx={{ my: 2 }} />
                    <FiltrosInventario
                        filtros={filtros}
                        onAplicar={aplicarFiltros}
                        loading={loading}
                        totalResultados={inventarios.length}
                        conPreparador={false}
                    />
                </Box>

                {/* Contenido */}
                <Box sx={{ px: isMobile ? 2 : 3, pb: 3 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            No se pudieron cargar tus inventarios. Intenta nuevamente.
                        </Alert>
                    )}

                    {isMobile ? (
                        <MisInventariosMobileList rows={inventarios} loading={loading} onVer={handleVer} />
                    ) : (
                        <DataGrid
                            rows={inventarios}
                            columns={columns}
                            localeText={DATA_GRID_LOCALE_TEXT}
                            autoHeight
                            loading={loading}
                            disableRowSelectionOnClick
                            disableColumnMenu
                            initialState={DATA_GRID_INITIAL_STATE}
                            pageSizeOptions={DATA_GRID_PAGE_SIZE_OPTIONS}
                            slots={{ noRowsOverlay: NoRowsOverlay }}
                            slotProps={DATA_GRID_SLOT_PROPS}
                            sx={DATA_GRID_SX}
                        />
                    )}
                </Box>
            </Card>

            <DrawerDetalleInventario
                open={drawer.abierto}
                onClose={drawer.cerrar}
                inventario={drawer.inventario}
                loading={drawer.loading}
                error={drawer.error}
            />
        </Box>
    );
};

export default MisInventarios;
